import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { useConfirm } from '@/components/ui/confirm';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Field, Select } from '@/components/ui/field';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { TD, TH, THead, TR, Table, TableWrap } from '@/components/ui/table';
import { RiskSeverity, apiError, riskSeverityTone, type RiskReviewer } from './lib';
import { useStaffNames } from './useStaffNames';

/**
 * Who is told a rule fired — a venue's own explicit nomination list, never
 * "everyone holding a permission". Nominating someone here does not, by
 * itself, let them see the case queue: that is `risk.review`, granted
 * through the ordinary role system. A push notification to an owner who
 * never opens the queue is a valid, intended configuration.
 */
export default function ReviewersTab() {
  const { t } = useTranslation();
  const confirm = useConfirm().confirm;
  const { nameOf, options } = useStaffNames();

  const [rows, setRows] = useState<RiskReviewer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userId, setUserId] = useState('');
  const [minSeverity, setMinSeverity] = useState<RiskSeverity>(RiskSeverity.MEDIUM);
  const [saving, setSaving] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    api
      .get<RiskReviewer[]>('/risk/reviewers')
      .then(({ data }) => setRows(data))
      .catch((e) => setError(apiError(e, t('risk.reviewers.loadFailed'))))
      .finally(() => setLoading(false));
  }, [t]);

  useEffect(load, [load]);

  const alreadyReviewer = new Set(rows.map((r) => r.userId));
  const candidates = options.filter((o) => !alreadyReviewer.has(o.id));

  const nominate = async () => {
    if (!userId) return;
    setSaving(true);
    try {
      await api.put('/risk/reviewers', { userId, minSeverity });
      toast.success(t('risk.reviewers.nominated'));
      setUserId('');
      load();
    } catch (e) {
      toast.error(apiError(e, t('risk.reviewers.nominateFailed')));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (reviewer: RiskReviewer) => {
    const ok = await confirm({
      title: t('risk.reviewers.removeConfirmTitle'),
      body: t('risk.reviewers.removeConfirmBody', { name: nameOf(reviewer.userId) }),
      confirmLabel: t('common.remove'),
      tone: 'danger',
    });
    if (!ok) return;
    setRemovingId(reviewer.userId);
    try {
      await api.delete(`/risk/reviewers/${reviewer.userId}`);
      toast.success(t('risk.reviewers.removed'));
      load();
    } catch (e) {
      toast.error(apiError(e, t('risk.reviewers.removeFailed')));
    } finally {
      setRemovingId(null);
    }
  };

  return (
    <div className="space-y-3">
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">{t('risk.reviewers.nominateTitle')}</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap items-end gap-2">
          <div className="min-w-48 flex-1">
            <Field label={t('risk.reviewers.person')}>
              <Select value={userId} onChange={(e) => setUserId(e.target.value)}>
                <option value="">{t('risk.reviewers.pickPerson')}</option>
                {candidates.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
          <Field label={t('risk.reviewers.minSeverity')} hint={t('risk.reviewers.minSeverityHint')}>
            <Select value={minSeverity} onChange={(e) => setMinSeverity(e.target.value as RiskSeverity)}>
              {Object.values(RiskSeverity).map((s) => (
                <option key={s} value={s}>
                  {t(`risk.severity.${s}`)}
                </option>
              ))}
            </Select>
          </Field>
          <Button onClick={() => void nominate()} disabled={!userId || saving}>
            {t('risk.reviewers.nominate')}
          </Button>
        </CardContent>
      </Card>

      {loading ? (
        <LoadingState label={t('common.loading')} />
      ) : error ? (
        <ErrorState message={error} onRetry={load} />
      ) : rows.length === 0 ? (
        <EmptyState title={t('risk.reviewers.empty')} hint={t('risk.reviewers.emptyHint')} />
      ) : (
        <TableWrap>
          <Table>
            <THead>
              <TR>
                <TH>{t('risk.reviewers.person')}</TH>
                <TH>{t('risk.reviewers.minSeverity')}</TH>
                <TH />
              </TR>
            </THead>
            <tbody>
              {rows.map((reviewer) => (
                <TR key={reviewer.id}>
                  <TD className="font-medium">{nameOf(reviewer.userId)}</TD>
                  <TD>
                    <Badge tone={riskSeverityTone(reviewer.minSeverity)}>
                      {t(`risk.severity.${reviewer.minSeverity}`)}
                    </Badge>
                  </TD>
                  <TD className="text-end">
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={removingId === reviewer.userId}
                      onClick={() => remove(reviewer)}
                    >
                      {t('common.remove')}
                    </Button>
                  </TD>
                </TR>
              ))}
            </tbody>
          </Table>
        </TableWrap>
      )}
    </div>
  );
}
